import type {
  ConfirmResponse,
  ExtendSlipResponse,
  ConversationTurn,
  Locale,
  LocaleBundle,
  ParseResponse,
  PlaceBetRequest,
  PlaceBetResponse,
  PlayerAccount,
  SessionResponse,
} from '@geni/contracts';
import type { OddsFormat } from '@geni/contracts/odds-format';
import type { WidgetErrorKind } from './state.js';

/**
 * Talking to the API.
 *
 * Thin on purpose. The widget ships to a public page, so everything it knows is
 * readable by anyone who opens developer tools — which means there is nothing
 * here worth hiding and nothing here worth trusting. The publishable key is
 * public by design; the session token is short-lived and bound to the origin.
 *
 * The one judgement this file makes is turning an HTTP status into an error the
 * string tables have a sentence for. A bettor should be told "too many requests
 * just now" rather than "429", and never anything about our internals.
 */

export class WidgetApiError extends Error {
  constructor(readonly kind: WidgetErrorKind, readonly status: number) {
    super(`geni api: ${kind} (${status})`);
    this.name = 'WidgetApiError';
  }
}

function kindForStatus(status: number): WidgetErrorKind {
  if (status === 429) return 'RATE_LIMITED';
  /**
   * The operator's odds feed, not us.
   *
   * Worth its own sentence: "something went wrong" reads as a broken
   * assistant, and what actually happened is that the board the bettor was
   * about to bet into could not be read for a moment. The advice differs too —
   * this one is worth trying again straight away.
   */
  if (status === 503) return 'BOARD';
  /**
   * A demo link that has spent its budget.
   *
   * "Something went wrong" is the wrong sentence twice over: nothing went
   * wrong, and the person reading it cannot fix anything by trying again. The
   * link did what it was minted to do, and the only thing that helps is a new
   * one from whoever sent it — so that is what it says.
   */
  if (status === 402) return 'SPENT';
  // 401 is a session that expired or was never valid. Either way the fix is the
  // same and the bettor is told to reload rather than to log in — they never
  // logged in here in the first place.
  if (status === 401 || status === 403) return 'SESSION';
  return 'GENERIC';
}

export interface WidgetClientOptions {
  baseUrl: string;
  publishableKey: string;
  /**
   * The demo link this page was opened from, when it was opened from one.
   *
   * A budget with a name rather than a credential: it says which purse the
   * conversation spends from. Absent on an operator's own site, which has no
   * such link and never will — this is for a URL handed to a stranger.
   */
  demoLink?: string;
  fetch?: typeof globalThis.fetch;
}

export class WidgetClient {
  private token: string | null = null;
  private readonly fetchImpl: typeof globalThis.fetch;

  constructor(private readonly options: WidgetClientOptions) {
    this.fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
  }

  private get headers(): Record<string, string> {
    return {
      'content-type': 'application/json',
      'x-geni-key': this.options.publishableKey,
      ...(this.token === null ? {} : { 'x-geni-session': this.token }),
    };
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await this.fetchImpl(`${this.options.baseUrl}${path}`, {
      ...init,
      /**
       * Never the bettor's cookies, whatever the deployment shape.
       *
       * Everything here authenticates by header — the publishable key and the
       * session token — and nothing on our side reads a cookie at all. Left
       * unset, `fetch` defaults to `same-origin`, which is harmless while the
       * API is on another host and wrong the moment a supplier reverse-proxies
       * us under the casino's own origin. That is a common integration shape,
       * and it would send us their bettors' sportsbook cookies: identity we do
       * not want, did not ask for, and would then have to account for.
       *
       * The operator's own endpoints below keep `same-origin` deliberately —
       * their cookie is how they know who is asking. Their cookie goes, ours
       * does not, and now that holds by instruction rather than by topology.
       * Found in a security review, 4 September.
       */
      credentials: 'omit',
      headers: { ...this.headers, ...(init.headers as Record<string, string> | undefined) },
    });

    if (!response.ok) throw new WidgetApiError(kindForStatus(response.status), response.status);

    // 204 carries no body, and `json()` on an empty one throws. Every call
    // here returned something until feedback, which has nothing to say back —
    // so this is the difference between a working tap and one that reports an
    // error after succeeding.
    if (response.status === 204) return undefined as T;

    return (await response.json()) as T;
  }

  async openSession(): Promise<SessionResponse> {
    const session = await this.request<SessionResponse>('/v1/sessions', {
      method: 'POST',
      body: JSON.stringify({
        publishableKey: this.options.publishableKey,
        ...(this.options.demoLink === undefined ? {} : { demoLink: this.options.demoLink }),
      }),
    });
    this.token = session.token;
    return session;
  }

  /** Strings and the operator's regulatory profile, in one answer. */
  async localeBundle(locale: Locale): Promise<LocaleBundle> {
    return this.request<LocaleBundle>(`/v1/i18n/${encodeURIComponent(locale)}`);
  }

  async parse(
    text: string,
    eventIds: readonly string[],
    locale: Locale,
    /**
     * Earlier turns, oldest first, already trimmed by the caller.
     *
     * Optional so the single-shot call still compiles and still works — an
     * empty history is exactly the behaviour this method had before, which is
     * what the server treats as the default.
     */
    history: readonly ConversationTurn[] = [],
    /**
     * A slip on screen that this sentence adds to, rather than replaces.
     *
     * Sent by the field under a priced slip and by nothing else. Omitted
     * entirely when absent, so the ordinary composer sends exactly the body it
     * always did.
     */
    addTo?: string,
    /**
     * How the page shows prices, so "around +150" is read as the bettor meant.
     *
     * Omitted when decimal, which is what the server assumes without it — so a
     * page that never set a format sends exactly the body it always did.
     */
    oddsFormat: OddsFormat = 'decimal',
  ): Promise<ParseResponse> {
    return this.request<ParseResponse>('/v1/parse', {
      method: 'POST',
      body: JSON.stringify({
        text,
        eventIds,
        locale,
        history,
        ...(addTo === undefined ? {} : { addTo }),
        ...(oddsFormat === 'decimal' ? {} : { oddsFormat }),
      }),
    });
  }

  /**
   * What the bettor thought of an answer.
   *
   * Deliberately not `parse`. Everything else typed in this widget goes there,
   * is handed to a model and comes back as a search for a bet — a complaint
   * down that path would be answered with a hunt for a fixture called "this is
   * useless", and would cost a model call to do it.
   *
   * Failures are swallowed by the caller rather than surfaced: the tap has
   * already been acknowledged on screen, and an error message about an opinion
   * that did not save would be a worse thing to read than the opinion was
   * worth sending.
   */
  async feedback(
    input: {
      verdict: 'HELPFUL' | 'NOT_HELPFUL' | 'MESSAGE';
      requestId?: string;
      reason?: 'WRONG_MATCH' | 'WRONG_BET' | 'MISUNDERSTOOD';
      text?: string;
    },
    /**
     * The language the bettor was reading when they wrote.
     *
     * Sent because nothing else on this request carries it, and the operator's
     * default is what the server falls back to — so a Russian complaint on a
     * Russian panel was being filed as English. Harmless per record and wrong
     * in aggregate, which is the only way this log is ever read: "which
     * language do people complain in" would have answered confidently and
     * incorrectly.
     */
    locale?: string,
  ): Promise<void> {
    await this.request<void>('/v1/feedback', {
      method: 'POST',
      body: JSON.stringify(input),
      ...(locale === undefined ? {} : { headers: { 'x-geni-locale': locale } }),
    });
  }

  /**
   * One more selection on a slip already in hand.
   *
   * Cheapest request the widget makes: the bettor pressed a code we drew for
   * them, so there is nothing to interpret and no model to wake. A refusal
   * comes back as data rather than as a throw — the slip they hold is untouched
   * and still confirmable, and the panel says so instead of clearing.
   */
  async extend(
    slipId: string,
    refs: readonly string[],
    /**
     * The language to name the new leg in.
     *
     * The route has always read `x-geni-locale` and nothing ever sent it, so
     * every extended slip came back in the operator's default. On a Russian
     * panel that put "Match Result / Over 2.5" under "Исход матча" — the same
     * leg, named two ways, depending on which button had added it.
     */
    locale?: string,
  ): Promise<ExtendSlipResponse> {
    return this.request<ExtendSlipResponse>('/v1/slips/extend', {
      method: 'POST',
      body: JSON.stringify({ slipId, refs }),
      ...(locale === undefined ? {} : { headers: { 'x-geni-locale': locale } }),
    });
  }

  /**
   * Take one leg back off, and hand back what the rest is now worth.
   *
   * Named by selection id rather than by an opaque ref, and that is not the
   * loose end it looks like: it names a leg the bettor is already holding, and
   * the server checks it against a slip loaded from its own store for this
   * session. See the route.
   */
  /** Same as extend, and the locale travels for the same reason. */
  async reduce(
    slipId: string,
    selectionIds: readonly string[],
    locale?: string,
  ): Promise<ExtendSlipResponse> {
    return this.request<ExtendSlipResponse>('/v1/slips/reduce', {
      method: 'POST',
      body: JSON.stringify({ slipId, selectionIds }),
      ...(locale === undefined ? {} : { headers: { 'x-geni-locale': locale } }),
    });
  }

  async confirm(slipId: string): Promise<ConfirmResponse> {
    return this.request<ConfirmResponse>(`/v1/slips/${encodeURIComponent(slipId)}/confirm`, {
      method: 'POST',
    });
  }

  /**
   * Hand the signed bet to the operator's own betting engine.
   *
   * The one request in this file that does not go to our API, and the
   * differences are all deliberate:
   *
   * **None of our headers go with it.** The publishable key and the session
   * token identify a Geni session and mean nothing to a casino's backend;
   * sending them would leak our credentials to a third party's access log for
   * no purpose.
   *
   * **`credentials: 'same-origin'`, so their session cookie does.** This is how
   * the operator knows whose account to debit — the widget never learns who the
   * bettor is, never holds an account identifier, and could not name one if the
   * endpoint asked. Which is also why the URL must be same-origin with the host
   * page: a cross-origin endpoint gets no cookie, so it would have no idea who
   * was betting, and the bet would be refused rather than misplaced.
   *
   * **A refusal is not an error.** "There wasn't enough in the account" comes
   * back as a 200 with a reason, because it is the bookmaker doing its job.
   * Only a genuine transport failure throws.
   */
  /**
   * Ask the operator's page what the bettor holds.
   *
   * The same journey as `placeBet` below and for the same reason: their cookie
   * goes, ours does not, and the operator resolves who is asking. The widget
   * never learns the bettor's identity and our API never sees the number.
   *
   * A failure is not an error worth showing anybody. An operator who has not
   * built this endpoint, one that is briefly down, one that answers for a
   * logged-out visitor — all mean the same thing to everything downstream: no
   * account, so no affordability check, which is exactly how the widget
   * behaved before this existed. Null, and carry on.
   */
  async fetchAccount(url: string): Promise<PlayerAccount | null> {
    try {
      const response = await this.fetchImpl(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { accept: 'application/json' },
      });
      if (!response.ok) return null;

      return readAccount(await response.json());
    } catch {
      return null;
    }
  }

  async placeBet(url: string, payload: PlaceBetRequest): Promise<PlaceBetResponse> {
    const response = await this.fetchImpl(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });

    if (!response.ok) throw new WidgetApiError(kindForStatus(response.status), response.status);
    return (await response.json()) as PlaceBetResponse;
  }
}

/**
 * The operator's answer, checked by hand rather than by a schema.
 *
 * `PlayerAccountSchema` exists and says exactly this, and importing it here
 * would drag the whole validation library into every operator's page — which
 * is the thing `apps/widget/test/stake.test.ts` forbids and is right to. Five
 * fields is cheaper to write twice than to ship a parser for.
 *
 * Everything is checked because everything is arithmetic downstream: a balance
 * arriving as the string "50" would compare against a stake perfectly happily
 * and tell somebody they could afford nine of them. The defaults match the
 * schema's, so an operator publishing only a balance behaves the same either
 * way.
 */
function readAccount(value: unknown): PlayerAccount | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;

  const balance = raw['balance'];
  const currency = raw['currency'];
  if (typeof balance !== 'number' || !Number.isFinite(balance) || balance < 0) return null;
  if (typeof currency !== 'string' || currency.length !== 3) return null;

  const positive = (candidate: unknown, fallback: number): number =>
    typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0
      ? candidate
      : fallback;

  return {
    balance,
    currency: currency as PlayerAccount['currency'],
    minStake: positive(raw['minStake'], 1),
    maxStake: positive(raw['maxStake'], 10_000),
    topUpAvailable: raw['topUpAvailable'] === true,
  };
}

/**
 * The language to ask for.
 *
 * The operator's own choice wins, because their page knows which language it is
 * written in. Failing that, the browser's — which is a preference the player set
 * and is the right default for a *language*.
 *
 * It is emphatically not how the jurisdiction is decided. That comes from the
 * operator's licence, server-side, and nothing in this file can influence it.
 */
export function preferredLocale(
  configured: string | null | undefined,
  navigatorLanguage: string | undefined,
  fallback: string,
): Locale {
  const candidate = configured ?? navigatorLanguage ?? fallback;
  return (candidate.trim() === '' ? fallback : candidate) as Locale;
}
